/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationService } from '@alfresco/adf-core';
import { ContentApiService, DateInputComponent, PageHeaderComponent, PageStateComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { forkJoin, from, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, toArray } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DmsField, DmsMetadataService, DmsOption } from '../../upload/dms-metadata.service';
import { LocalFilePreviewComponent } from '../../upload/local-file-preview.component';
import { ProcessDefinition, TemplateInstance, WorkflowService, WorkflowTemplate } from '../workflow.service';
import { WorkflowDialogComponent } from '../workflow-dialog.component';
import { flowSteps } from '../flow-graph';

/** A card on step 1 — either a custom template or an engine process-definition. */
interface WfCard {
  kind: 'template' | 'engine';
  id: string;
  /** Process-definition key used to start the process, or template id. */
  definitionId: string;
  version: number;
  name: string;
  description: string;
  steps: number;
  slaDays?: number;
  category: string;
  /** The engine key a template's first step will run on, or the def key itself. */
  engineKey: string;
  template?: WorkflowTemplate;
}

/** A queued upload inside the wizard. */
interface WfFile {
  file: File;
  nodeId?: string;
  state: 'ready' | 'uploading' | 'done' | 'failed';
  error?: string;
  role: 'primary' | 'attachment';
}

const ACCEPTED = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.jpg', '.jpeg', '.png', '.bmp', '.gif', '.webp'];
const MAX_FILE_BYTES = 100 * 1024 * 1024;
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp']);

const BADGES: Record<string, string> = {
  pdf: 'PDF',
  doc: 'W',
  docx: 'W',
  xls: 'X',
  xlsx: 'X',
  ppt: 'P',
  pptx: 'P',
  txt: 'TXT',
  jpg: 'IMG',
  jpeg: 'IMG',
  png: 'IMG',
  bmp: 'IMG',
  gif: 'IMG',
  webp: 'IMG'
};

const KNOWN_FIELDS = new Set([
  'dms:documentCode',
  'dms:documentType',
  'dms:documentDate',
  'dms:department',
  'dms:documentOwner',
  'dms:status',
  'dms:confidentiality'
]);

type Step = 1 | 2 | 3 | 4;

/**
 * Start-workflow wizard (WF-01..WF-04): pick a real definition or template,
 * queue the documents with a local preview, fill the metadata the repository
 * model asks for plus the workflow's own fields, review the path the instance
 * will take, then file the documents and start the Activiti process for real.
 *
 * Definitions come from two real sources and nothing else: enabled templates
 * built in Workflow Management that this viewer may see
 * ({@link WorkflowService.visibleStartableTemplates}) and the engine's
 * published process-definitions minus the ones Alfresco ships
 * ({@link WorkflowService.listProcessDefinitions}). Drafts, disabled and
 * restricted templates never reach the list.
 *
 * The chosen `workflowDefinitionId`/`workflowVersion` are kept on the
 * component for the whole wizard and land in the review step's summary.
 */
@Component({
  selector: 'app-workflow-wizard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressBarModule,
    PageHeaderComponent,
    PageStateComponent,
    LocalFilePreviewComponent,
    DateInputComponent,
    ThaiDatePipe,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './workflow-wizard.component.html',
  styles: []
})
export class WorkflowWizardComponent implements OnInit {
  private readonly wf = inject(WorkflowService);
  private readonly dms = inject(DmsMetadataService);
  private readonly contentApi = inject(ContentApiService);
  private readonly dialog = inject(MatDialog);
  private readonly notification = inject(NotificationService);

  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  readonly accept = ACCEPTED.join(',');
  readonly step = signal<Step>(1);
  readonly busy = signal(false);
  readonly starting = signal(false);
  readonly loadingDefs = signal(true);
  readonly schemaMissing = signal(false);

  // ---- step 1: pick a workflow -----------------------------------------------

  readonly cards = signal<WfCard[]>([]);
  readonly selected = signal<WfCard | null>(null);
  search = '';
  category = '';
  statusFilter: 'all' | 'ready' = 'all';

  readonly categories = computed(() =>
    [
      ...new Set(
        this.cards()
          .map((c) => c.category)
          .filter(Boolean)
      )
    ].sort()
  );

  /** search/category are plain ngModel fields — a method, so CD re-evaluates. */
  filtered(): WfCard[] {
    const term = this.search.trim().toLowerCase();
    return this.cards().filter(
      (c) =>
        (!this.category || c.category === this.category) &&
        (this.statusFilter === 'all' || this.isReady(c)) &&
        (!term || c.name.toLowerCase().includes(term) || c.description.toLowerCase().includes(term))
    );
  }

  /**
   * Whether the card can start as it stands. An engine definition takes its
   * assignee from this wizard; a template needs one on the step it opens with,
   * or starting it fails after the documents were already uploaded.
   */
  isReady(card: WfCard): boolean {
    if (card.kind !== 'template') {
      return true;
    }
    // a published drawn flow carries its own assignees, checked when it was published
    if (WorkflowService.runsAsProcess(card.template)) {
      return true;
    }
    const first = this.wf.nextRunnableStep(card.template, 0, {});
    return first >= 0 && !!card.template?.steps[first]?.assignee;
  }

  // ---- step 2: documents ------------------------------------------------------

  readonly queue = signal<WfFile[]>([]);
  readonly selectedIndex = signal(-1);
  readonly selectedFile = computed(() => this.queue()[this.selectedIndex()] ?? null);
  readonly dragging = signal(false);

  // ---- step 3: metadata + workflow fields -------------------------------------

  documentTypes: DmsOption[] = [];
  statuses: DmsOption[] = [];
  confidentialities: DmsOption[] = [];
  readonly extraFields = signal<DmsField[]>([]);
  extra: Record<string, string> = {};

  meta = {
    title: '',
    documentCode: '',
    documentType: '',
    documentDate: '',
    department: '',
    documentOwner: '',
    status: '',
    confidentiality: '',
    description: ''
  };

  /** Workflow-side fields every definition shares. */
  flow = {
    priority: 2,
    dueAt: '',
    message: ''
  };

  /** Assignee for engine definitions (templates carry their own per step). */
  assignee = '';
  assigneeLabel = '';
  readonly touched = signal(false);

  /** Whether the picked engine definition wants a person or a group. */
  readonly needsAssignee = computed(() => {
    const card = this.selected();
    if (!card || card.kind !== 'engine') {
      return 'none';
    }
    return /pooled|group/i.test(card.engineKey) ? 'group' : 'person';
  });

  readonly steps: { n: Step; key: string }[] = [
    { n: 1, key: 'DMS.WIZARD.STEP_PICK' },
    { n: 2, key: 'DMS.WIZARD.STEP_UPLOAD' },
    { n: 3, key: 'DMS.WIZARD.STEP_DATA' },
    { n: 4, key: 'DMS.WIZARD.STEP_REVIEW' }
  ];

  ngOnInit(): void {
    this.loadDefinitions();
    this.dms.schema().subscribe({
      next: (schema) => {
        if (!schema.available) {
          this.schemaMissing.set(true);
          return;
        }
        const options = (f?: DmsField) => f?.options ?? [];
        this.documentTypes = options(schema.fields['dms:documentType']);
        this.statuses = options(schema.fields['dms:status']);
        this.confidentialities = options(schema.fields['dms:confidentiality']);
        this.meta.status = schema.fields['dms:status']?.defaultValue ?? '';
        this.meta.confidentiality = schema.fields['dms:confidentiality']?.defaultValue ?? '';
        const extra = Object.values(schema.fields).filter((f) => f.id.startsWith('dms:') && !KNOWN_FIELDS.has(f.id));
        this.extraFields.set(extra);
        for (const f of extra) {
          this.extra[f.id] = f.defaultValue ?? '';
        }
      },
      error: () => this.schemaMissing.set(true)
    });

    // a document handed over from a files list starts the wizard on step 2
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const nodeId = params.get('nodeId');
      if (nodeId) {
        this.attachExistingNode(nodeId);
      }
    });
  }

  private loadDefinitions(): void {
    this.loadingDefs.set(true);
    forkJoin({
      defs: this.wf.listProcessDefinitions().pipe(catchError(() => of([] as ProcessDefinition[]))),
      // gate failure stays closed — restricted flows never leak on error
      templates: this.wf.visibleStartableTemplates().pipe(catchError(() => of(this.wf.getStartableTemplates().filter((t) => !t.restricted))))
    }).subscribe(({ defs, templates }) => {
      const cards: WfCard[] = [];

      for (const tpl of templates) {
        const graph = WorkflowService.publishedGraph(tpl);
        cards.push({
          kind: 'template',
          id: tpl.id,
          definitionId: tpl.id,
          version: tpl.version ?? 1,
          name: tpl.name,
          description: tpl.description ?? '',
          steps: graph ? flowSteps(graph).length : tpl.steps.length,
          slaDays: tpl.slaDays,
          category: tpl.category || 'custom',
          engineKey: 'template',
          template: tpl
        });
      }

      for (const def of defs) {
        cards.push({
          kind: 'engine',
          id: def.id,
          definitionId: def.id,
          version: Number(String(def.id).split(':')[1]) || 1,
          name: def.name || def.key,
          description: '',
          steps: 1,
          category: 'system',
          engineKey: def.key
        });
      }

      this.cards.set(cards);
      this.loadingDefs.set(false);
    });
  }

  // ---- navigation --------------------------------------------------------------

  pick(card: WfCard): void {
    this.selected.set(card);
  }

  /** Step 3 offers the chosen flow as a select — picking there re-picks it. */
  pickById(id: string): void {
    const card = this.cards().find((c) => c.id === id);
    if (card) {
      this.selected.set(card);
    }
  }

  next(): void {
    const s = this.step();
    if (s === 1 && this.selected()) {
      this.step.set(2);
    } else if (s === 2 && this.queue().length) {
      this.step.set(3);
    } else if (s === 3) {
      this.touched.set(true);
      if (this.metadataValid()) {
        this.step.set(4);
      }
    }
  }

  back(): void {
    this.step.update((s) => (s > 1 ? ((s - 1) as Step) : s));
  }

  stepClass(n: number): string {
    if (n === this.step()) {
      return 'aca-wz__step--current';
    }
    return n < this.step() ? 'aca-wz__step--done' : 'aca-wz__step--todo';
  }

  /** Required metadata answered; engine definitions also need their assignee. */
  metadataValid(): boolean {
    const base = !!(this.meta.title.trim() && this.meta.documentType && this.meta.documentDate && this.meta.department.trim() && this.flow.dueAt);
    const who = this.needsAssignee();
    return base && (who === 'none' || !!this.assignee);
  }

  // ---- step 2: files ------------------------------------------------------------

  onFileInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(Array.from(input.files ?? []));
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(false);
    this.addFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  addFiles(files: File[]): void {
    const dupes: string[] = [];
    const accepted = files.filter((f) => {
      const ext = f.name.split('.').pop()?.toLowerCase() ?? '';
      const ok = ACCEPTED.includes(`.${ext}`) && f.size <= MAX_FILE_BYTES;
      if (!ok) {
        dupes.push(f.name);
      }
      return ok;
    });
    const fresh = accepted.filter((f) => !this.queue().some((q) => q.file.name === f.name && q.file.size === f.size));
    if (dupes.length || fresh.length < accepted.length) {
      this.notification.showError(this.translate.instant('DMS.WIZARD.FILES_REJECTED'));
    }
    if (!fresh.length) {
      return;
    }
    this.queue.update((list) => [
      ...list,
      ...fresh.map((file, i) => ({
        file,
        state: 'ready' as const,
        // only the first file of an empty queue is the primary document
        role: list.length === 0 && i === 0 ? ('primary' as const) : ('attachment' as const)
      }))
    ]);
    if (this.selectedIndex() < 0) {
      this.selectedIndex.set(0);
    }
  }

  removeFile(index: number): void {
    this.queue.update((list) => list.filter((_, i) => i !== index));
    const sel = this.selectedIndex();
    if (sel === index) {
      this.selectedIndex.set(Math.min(index, this.queue().length - 1));
    } else if (sel > index) {
      this.selectedIndex.set(sel - 1);
    }
  }

  viewFile(index: number): void {
    this.selectedIndex.set(index);
  }

  /** Mark one file the primary document the workflow is about. */
  makePrimary(index: number): void {
    this.queue.update((list) => list.map((q, i) => ({ ...q, role: i === index ? 'primary' : 'attachment' })));
  }

  badge(file: File): string {
    return BADGES[file.name.split('.').pop()?.toLowerCase() ?? ''] ?? 'FILE';
  }

  /** Review step: the picked document type as its translated label, not the code. */
  docTypeLabel(): string {
    return DmsMetadataService.label(this.meta.documentType);
  }

  /** Review step: 1/2/3 reads as the named priority. */
  priorityLabel(): string {
    return this.flow.priority === 1 ? 'DMS.WIZARD.P_HIGH' : this.flow.priority === 3 ? 'DMS.WIZARD.P_LOW' : 'DMS.WIZARD.P_MEDIUM';
  }

  /** Colour tone of the attachment tile, by extension. */
  fileTone(file: File): string {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (ext === 'pdf') {
      return 'pdf';
    }
    if (['doc', 'docx'].includes(ext)) {
      return 'doc';
    }
    if (['xls', 'xlsx', 'csv', 'ppt', 'pptx'].includes(ext)) {
      return 'sheet';
    }
    return IMAGE_EXTENSIONS.has(ext) ? 'img' : 'other';
  }

  /** Leading icon inside a schema-driven extra field's input. */
  fieldIcon(field: DmsField): string {
    if (field.id === 'dms:amount') {
      return 'payments';
    }
    const kind = this.inputKind(field);
    return kind === 'list' ? 'label' : kind === 'date' ? 'event' : kind === 'number' ? 'pin' : 'edit_note';
  }

  isImage(file: File): boolean {
    return IMAGE_EXTENSIONS.has(file.name.split('.').pop()?.toLowerCase() ?? '');
  }

  sizeLabel(bytes: number): string {
    if (bytes >= 1048576) {
      return `${(bytes / 1048576).toFixed(1)} MB`;
    }
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  /** A document already in the repository can seed the wizard (context action). */
  private attachExistingNode(nodeId: string): void {
    from(this.contentApi.getNode(nodeId)).subscribe({
      next: (res: any) => {
        const node = res?.entry;
        if (!node) {
          return;
        }
        this.queue.set([{ file: new File([], node.name), nodeId: node.id, state: 'ready', role: 'primary' }]);
        this.selectedIndex.set(0);
      },
      error: () => undefined
    });
  }

  // ---- step 3: assignment ---------------------------------------------------------

  pickAssignee(): void {
    const group = this.needsAssignee() === 'group';
    const ref = this.dialog.open(WorkflowDialogComponent, {
      data: {
        mode: group ? 'pickGroup' : 'pickPerson',
        title: this.translate.instant(group ? 'DMS.WIZARD.PICK_GROUP' : 'DMS.WIZARD.PICK_PERSON')
      }
    });
    ref.afterClosed().subscribe((p) => {
      if (p?.id) {
        this.assignee = p.id;
        this.assigneeLabel = p.displayName;
      }
    });
  }

  // ---- step 4: review + start -------------------------------------------------------

  /** The path preview — real template steps, or the engine def's own name. */
  pathPreview(): { name: string; who: string; type: string }[] {
    const card = this.selected();
    if (!card) {
      return [];
    }
    const graph = card.kind === 'template' ? WorkflowService.publishedGraph(card.template) : null;
    if (graph) {
      // a drawn flow branches; this lists its approvals and tasks in the order the flow reaches them
      return flowSteps(graph).map((n) => ({
        name: n.name,
        who: n.assigneeType === 'initiator' ? this.translate.instant('DMS.FLOW.WHO_INITIATOR') : n.assigneeLabel || n.assignee || '—',
        type: n.kind === 'approval' ? 'review' : 'task'
      }));
    }
    if (card.kind === 'template' && card.template) {
      return card.template.steps.map((s) => ({ name: s.name, who: s.assigneeLabel || s.assignee || '—', type: s.type }));
    }
    return [{ name: card.name, who: this.assigneeLabel || this.assignee || '—', type: 'review' }];
  }

  /** Files → Alfresco → metadata → Activiti process, in that order. */
  start(): void {
    const card = this.selected();
    if (!card || this.starting()) {
      return;
    }
    // refuse a template with nobody on its opening step before anything is
    // uploaded — failing afterwards left the files behind in the repository
    if (card.kind === 'template' && card.template && !WorkflowService.runsAsProcess(card.template)) {
      const vars = { ...this.documentProps(), ...this.launchExtras() };
      const first = this.wf.nextRunnableStep(card.template, 0, vars);
      if (!card.template.steps[first]?.assignee) {
        this.notification.showError(this.translate.instant('DMS.WIZARD.NO_ASSIGNEE'));
        return;
      }
    }
    this.starting.set(true);

    // upload every file that is not already a repository node
    const jobs = this.queue().map((q, i) => {
      if (q.nodeId) {
        return of({ index: i, nodeId: q.nodeId });
      }
      return this.dms.upload(q.file, '-my-').pipe(
        map((res: any) => ({ index: i, nodeId: String(res?.entry?.id ?? res?.id ?? '') })),
        catchError((e) => of({ index: i, nodeId: '', error: e?.message ?? 'upload failed' }))
      );
    });

    from(jobs)
      .pipe(
        concatMap((j) => j),
        toArray()
      )
      .subscribe({
        next: (results) => {
          const nodeIds = results.map((r) => r.nodeId).filter(Boolean);
          if (!nodeIds.length) {
            this.starting.set(false);
            this.notification.showError(this.translate.instant('DMS.WIZARD.UPLOAD_ALL_FAILED'));
            return;
          }
          // metadata lands on the primary document
          const props = this.documentProps();
          this.dms
            .applyMetadata(nodeIds[0], props)
            .pipe(catchError(() => of(null)))
            .subscribe(() => this.launch(card, nodeIds, props));
        },
        error: () => {
          this.starting.set(false);
          this.notification.showError(this.translate.instant('DMS.WIZARD.UPLOAD_ALL_FAILED'));
        }
      });
  }

  /** The metadata written to the primary document — also what step conditions read. */
  private documentProps(): Record<string, any> {
    const props: Record<string, any> = {
      'cm:title': this.meta.title,
      'cm:description': this.meta.description,
      'dms:documentCode': this.meta.documentCode,
      'dms:documentType': this.meta.documentType,
      'dms:department': this.meta.department,
      'dms:documentOwner': this.meta.documentOwner,
      'dms:status': this.meta.status,
      'dms:confidentiality': this.meta.confidentiality
    };
    if (this.meta.documentDate) {
      props['dms:documentDate'] = this.meta.documentDate;
    }
    for (const [k, v] of Object.entries(this.extra)) {
      if (v !== '') {
        props[k] = v;
      }
    }
    return props;
  }

  /** Priority and due date, sent with whichever process starts. */
  private launchExtras(): Record<string, any> {
    const extra: Record<string, any> = { bpm_priority: this.flow.priority };
    if (this.flow.dueAt) {
      extra.bpm_workflowDueDate = new Date(this.flow.dueAt).toISOString();
    }
    return extra;
  }

  /** Starts the real process after the documents exist; `docVars` feeds step conditions. */
  private launch(card: WfCard, nodeIds: string[], docVars: Record<string, any> = {}): void {
    const description = this.flow.message || this.meta.title;
    const extra = this.launchExtras();

    const done = (proc: { id: string }) => {
      this.starting.set(false);
      this.notification.showInfo(this.translate.instant('DMS.WIZARD.STARTED', { p0: proc.id }));
      void this.router.navigate(['/workflows/instances', proc.id]);
    };
    const fail = (e: any) => {
      this.starting.set(false);
      // eslint-disable-next-line no-console
      console.error('Start workflow failed', e);
      this.notification.showError(this.translate.instant('DMS.WIZARD.START_FAILED'));
      return of(null);
    };

    // a published drawn flow is a process definition of its own: the engine
    // runs every step, so there is no step chain to record
    const deployment = card.kind === 'template' && card.template?.graph ? card.template.deployment : undefined;
    if (card.template && deployment) {
      const tpl = card.template;
      this.wf
        .startProcess(deployment.key, { bpm_workflowDescription: `${tpl.name} — ${description}`, ...extra }, nodeIds)
        .pipe(catchError(fail))
        .subscribe((proc: any) => {
          if (proc) {
            done(proc);
          }
        });
      return;
    }

    if (card.kind === 'template' && card.template) {
      const tpl = card.template;
      const vars = { ...docVars, ...extra };
      const first = this.wf.nextRunnableStep(tpl, 0, vars);
      const step = first >= 0 ? tpl.steps[first] : undefined;
      if (!step?.assignee) {
        this.starting.set(false);
        this.notification.showError(this.translate.instant('DMS.WIZARD.NO_ASSIGNEE'));
        return;
      }
      this.wf
        .startStep(step, `${tpl.name} — ${description}`, nodeIds, extra)
        .pipe(
          switchMap((proc) => this.wf.processTasks(proc.id).pipe(map((tks) => ({ proc, taskId: tks[0]?.id })))),
          catchError(fail)
        )
        .subscribe((res: any) => {
          if (!res) {
            return;
          }
          const inst: TemplateInstance = {
            id: `inst-${Date.now()}`,
            templateId: tpl.id,
            templateName: tpl.name,
            templateVersion: tpl.version ?? 1,
            nodeIds,
            currentStep: first,
            status: 'running',
            taskId: res.taskId,
            processId: res.proc.id,
            startedAt: new Date().toISOString(),
            history: [],
            vars
          };
          // the first step runs either way; without the record the later
          // steps would not follow it, so say so rather than stay quiet
          this.wf
            .createInstance(inst)
            .pipe(
              catchError(() => {
                this.notification.showWarning(this.translate.instant('DMS.WIZARD.FLOW_NOT_RECORDED'));
                return of(null);
              })
            )
            .subscribe(() => done(res.proc));
        });
      return;
    }

    // engine definition — assignee goes straight into the process variables
    const vars: Record<string, any> = {
      bpm_workflowDescription: description,
      ...extra
    };
    if (this.needsAssignee() === 'group') {
      vars.bpm_groupAssignee = `GROUP_${this.assignee.replace(/^GROUP_/, '')}`;
    } else if (this.assignee) {
      vars.bpm_assignee = this.assignee;
    }
    this.wf
      .startProcess(card.engineKey, vars, nodeIds)
      .pipe(catchError(fail))
      .subscribe((proc: any) => {
        if (proc) {
          done(proc);
        }
      });
  }

  /** The input an added metadata field needs — same rules as the upload page. */
  inputKind(field: DmsField): 'list' | 'date' | 'number' | 'boolean' | 'text' {
    if (field.options?.length) {
      return 'list';
    }
    if (field.dataType === 'd:date' || field.dataType === 'd:datetime') {
      return 'date';
    }
    if (['d:int', 'd:long', 'd:float', 'd:double', 'd:decimal'].includes(field.dataType)) {
      return 'number';
    }
    return field.dataType === 'd:boolean' ? 'boolean' : 'text';
  }

  /** What the field is called, as the settings screen named it. */
  fieldLabel(field: DmsField): string {
    return DmsMetadataService.label(field.id);
  }
}
